import React, { useState } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  FaHome,
  FaUsers,
  FaBoxOpen,
  FaMobileAlt,
  FaShieldAlt,
  FaSignOutAlt,
  FaUser,
  FaFileAlt,
  FaPlusCircle,
  FaClipboardList,
} from 'react-icons/fa'
import { useAuth } from '../contexts/AuthContext'

const Layout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const isAdmin = user?.role === 'admin'

  const navItems = [
    { label: 'Dashboard', path: '/', icon: FaHome },
    { label: 'Orders', path: '/orders', icon: FaBoxOpen },
    { label: 'Clearance', path: '/clearance', icon: FaFileAlt },
    ...(isAdmin ? [
      { label: 'Users', path: '/users', icon: FaUsers },
      { label: 'Create Shipment', path: '/shipments/create', icon: FaPlusCircle },
      { label: 'Mobile Config', path: '/mobile-config', icon: FaMobileAlt },
      { label: 'Sales', path: '/sales', icon: FaClipboardList },
    ] : []),
  ]

  const handleLogout = () => {
    logout()
    navigate('/signin')
  }

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <div className="flex min-h-screen bg-[#f8f9fa]">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky lg:top-0 z-50 h-screen w-[250px] bg-[#2c3e50] flex flex-col transition-transform duration-300 flex-shrink-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          }`}
      >
        {/* Brand */}
        <div className="px-6 py-5 border-b border-[#34495e]">
          <div className="flex items-center gap-3">
            <FaShieldAlt className="text-white text-xl" />
            <h1 className="text-white text-lg font-bold">Admin Panel</h1>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon
            const active = isActive(item.path)
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-6 py-3 text-sm transition-all duration-200 border-l-[3px] ${active
                  ? 'bg-white/15 border-white text-white'
                  : 'text-white/80 border-transparent hover:bg-white/10 hover:text-white'
                  }`}
              >
                <Icon className="text-base flex-shrink-0" />
                <span>{item.label}</span>
              </Link>
            )
          })}
        </nav>

        {/* User + Logout */}
        <div className="px-6 py-4 border-t border-[#34495e]">
          <div className="flex items-center gap-3 mb-3 text-white/80">
            <FaUser className="text-base flex-shrink-0" />
            <span className="text-sm truncate">{user?.name || user?.email || 'Admin'}</span>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 text-white text-sm border border-white/30 rounded hover:bg-white/10 transition-all duration-200"
          >
            <FaSignOutAlt />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-[#e9ecef] px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-[#2c3e50] p-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <FaShieldAlt className="text-[#2c3e50] text-xl" />
            <h1 className="text-[#2c3e50] text-lg font-bold">Admin Panel</h1>
          </div>
        </header>

        <main className="bg-[#f8f9fa] min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export default Layout
